/**
 * SVG Layer Overlay Extension for Map Facade
 *
 * Extends the map facade with createSvgLayerOverlay() for rendering stacked SVG layer
 * overlays on a Google Maps pane. This module is domain-agnostic: it handles the
 * OverlayView lifecycle, SVGZ loading, layer group management, pixel positioning, and
 * generic pointer event routing. All domain-specific lot rendering is delegated to the
 * `handlers.lotRenderer` object supplied by the feature layer.
 *
 * Architecture:
 * - Main SVG (overlayLayer pane): stacked site plan layers clipped to SvgMask
 * - Hit SVG (overlayMouseTarget pane): transparent rect capturing mouse events
 * - SVGZ files are fetched, decompressed, and cached globally by URL
 * - Layer groups are pre-created in sorted order; content is injected lazily on first show
 *
 * Performance:
 * - DecompressionStream + Response.text() decompresses SVGZ with zero manual chunk management
 * - Layer content is importNode-cloned from a parsed document into the live overlay SVG
 * - draw() exits early when pixel bounds haven't changed
 *
 * Lot rendering contract: see {@link LotRenderer} typedef.
 *
 * @module mapLibrary/svgOverlays
 */

import { computePixelBounds, hasPixelRectChanged, isDragGesture } from './overlayUtils';
import {
  applyPositionStyle,
  buildHitSvgLayer,
  buildMainSvgElement,
  buildLayersGroup,
  buildLayerGroups,
  injectSvgLayerContent,
  applyLayerClass,
  getOrCreateCommunityWrapper,
} from './overlayDom';
import fetchSvgzLayerText from './svgzLoader';

/**
 * Layer types that require live inline SVG so lot status classes and path ID
 * resolution can mutate the DOM after injection. Background layers (Grass,
 * Street, Water, etc.) are also injected inline and styled via CSS classes
 * applied by applyLayerClass (see overlayDom.js).
 */
const INTERACTIVE_LAYER_TYPES = new Set(['LotPolygons', 'MainLots']);

/**
 * Creates an SvgLayerOverlay by composing a google.maps.OverlayView instance.
 * All state is held in closure variables — no subclassing, no lazy class cache.
 *
 * @param {Object} initialSitePlan
 * @param {Object|null} initialInteractiveData
 * @param {Object} handlers
 * @param {Function} [handlers.onSitePlanClick] - Called with { sitePlanId } on click
 * @param {Object}   [handlers.classNames]      - CSS class name map
 * @param {LotRenderer} [handlers.lotRenderer]  - Lot domain callbacks (see {@link LotRenderer})
 * @param {Object} google - The loaded Google Maps API object
 * @returns {{ overlayView, setVisibleLayerTypes, syncData }}
 */
function buildSvgLayerOverlay(initialSitePlan, initialInteractiveData, handlers, google) {
  let sitePlan        = initialSitePlan;
  let interactiveData = initialInteractiveData;

  const onSitePlanClick = handlers?.onSitePlanClick ?? null;
  const onSitePlanEnter = handlers?.onSitePlanEnter ?? null;
  const onSitePlanLeave = handlers?.onSitePlanLeave ?? null;
  const classNames      = handlers?.classNames      ?? {};
  const lotRenderer     = handlers?.lotRenderer     ?? null;

  // DOM references
  let mainSvgElement        = null;
  let containerElement      = null;
  let communityWrapperElement = null;
  let hitSvgElement         = null;
  let hitRectElement        = null;
  let layerEntries          = new Map();

  // Runtime state
  let lastPixelRect  = null;
  let lotStatusIndex = new Map();
  let pointerDownPos = null;

  // Ready state — onAdd populates layerEntries asynchronously relative to setMap().
  // onReady(cb) queues a callback that fires once onAdd has completed so that
  // setVisibleLayerTypes() is never called on an empty layerEntries Map.
  let isAdded       = false;
  let readyCallback = null;

  // Bound listener references (kept for removal in onRemove)
  let boundHandleClick               = null;
  let boundHandleMouseDown           = null;
  let boundHandleMouseEnterContainer = null;
  let boundHandleMouseLeaveContainer = null;

  function buildMainSvg(sitePlanElement) {
    const { svgMask, svgLayers, viewBoxWidth, viewBoxHeight, sitePlanId } = sitePlanElement;
    const clipPathId   = `sp-svg-layers-mask-${sitePlanId}`;
    const sortedLayers = lotRenderer ? lotRenderer.sortLayers(svgLayers) : svgLayers;

    const svgElement  = buildMainSvgElement(viewBoxWidth, viewBoxHeight, clipPathId, svgMask, classNames.mask);
    const layersGroup = buildLayersGroup(svgMask, clipPathId);
    svgElement.appendChild(layersGroup);
    const entries = buildLayerGroups(sortedLayers, layersGroup);

    return { svgElement, entries };
  }

  function applyLotStatusClasses() {
    if (!mainSvgElement || !lotRenderer) {
      return;
    }
    lotRenderer.applyStatusClasses(mainSvgElement, lotStatusIndex);
  }

  function handleMouseDown(event) {
    pointerDownPos = { x: event.clientX, y: event.clientY };
  }

  function handleClick(event) {
    if (!onSitePlanClick) {
      return;
    }
    if (isDragGesture(pointerDownPos, event.clientX, event.clientY)) {
      return;
    }
    onSitePlanClick({ sitePlanId: sitePlan.sitePlanId });
  }

  function loadAndInjectLayerContent(layerType) {
    const entry = layerEntries.get(layerType);
    if (!entry) {
      return;
    }

    // Guard against concurrent fetches for the same layer (e.g. setVisibleLayerTypes
    // called twice before the first SVGZ response resolves). Without this, both calls
    // would see contentLoaded===false and inject content into the same group twice.
    if (entry.contentLoaded || entry.loadingPromise) {
      return;
    }

    entry.loadingPromise = fetchSvgzLayerText(entry.url).then((svgText) => {
      if (!svgText) {
        return;
      }
      if (!mainSvgElement || !mainSvgElement.isConnected) {
        return;
      }

      // All layer types are injected as live inline SVG into their <g> group so they
      // share the outer SVG's viewBox, coordinate space, and clip-path.
      // For background layers (Grass, Street, Water, …) fills are applied in JS after
      // injection; for interactive layers lot-status classes are applied instead.
      const injected = injectSvgLayerContent(svgText, entry.groupElement);
      if (!injected) {
        return;
      }

      if (INTERACTIVE_LAYER_TYPES.has(layerType)) {
        applyLotStatusClasses();
      } else {
        applyLayerClass(entry.groupElement, layerType, classNames.layerPrefix);
      }

      // Mark loaded only after all post-injection work succeeds so that a failure
      // in resolvePathIds or applyStatusClasses does not permanently suppress retry.
      entry.contentLoaded = true;
    }).catch(() => {
      // Silently ignore SVGZ load failures — the layer simply will not render.
    }).finally(() => {
      entry.loadingPromise = null;
    });
  }

  function buildHitSvg(sitePlanElement) {
    const { viewBoxWidth, viewBoxHeight, svgMask, sitePlanId } = sitePlanElement;
    const clipPathId = `sp-hit-mask-${sitePlanId}`;
    return buildHitSvgLayer(viewBoxWidth, viewBoxHeight, clipPathId, svgMask, classNames.mask);
  }

  // Separated from buildHitSvg so construction (pure-ish) and listener wiring
  // (effect) are traceable independently — onRemove only needs to know about
  // the refs captured here, not the element geometry.
  function bindHitSvgListeners(hitRect) {
    hitRectElement = hitRect;

    boundHandleMouseDown = (e) => handleMouseDown(e);
    hitRect.addEventListener('mousedown', boundHandleMouseDown);

    boundHandleMouseEnterContainer = () => {
      if (onSitePlanEnter) {
        onSitePlanEnter(sitePlan.communityId);
      }
    };
    boundHandleMouseLeaveContainer = () => {
      if (onSitePlanLeave) {
        onSitePlanLeave(sitePlan.communityId);
      }
    };
    hitRect.addEventListener('mouseenter', boundHandleMouseEnterContainer);
    hitRect.addEventListener('mouseleave', boundHandleMouseLeaveContainer);

    boundHandleClick = (e) => handleClick(e);
    hitRect.addEventListener('click', boundHandleClick);
  }

  const overlayView = new google.maps.OverlayView();

  // Returns the existing community wrapper div for communityId, or creates and
  // appends one to parentElement. Shared helper from overlayDom.js used by both
  // overlay strategies (static image and interactive SVG layers).
  function getOrCreateCommunityWrapperLocal(parentElement, communityId) {
    return getOrCreateCommunityWrapper(parentElement, 'sitePlanCommunityId', communityId);
  }

  overlayView.onAdd = function () {
    const panes = overlayView.getPanes();

    containerElement = document.createElement('div');
    if (classNames.container) {
      containerElement.className = classNames.container;
    }

    const { svgElement, entries } = buildMainSvg(sitePlan);
    mainSvgElement = svgElement;
    layerEntries   = entries;
    containerElement.appendChild(mainSvgElement);

    // One wrapper div per communityId is shared across all site plans of that
    // community; the active class is applied to this wrapper, not to individual
    // containers, so hover/shadow affects the whole community footprint.
    communityWrapperElement = getOrCreateCommunityWrapperLocal(
      panes.overlayLayer,
      String(sitePlan.communityId)
    );
    communityWrapperElement.appendChild(containerElement);

    if (interactiveData && lotRenderer) {
      const lotData  = lotRenderer.buildLotData(interactiveData.sitePlanLots);
      lotStatusIndex = lotData.lotStatusIndex;
      applyLotStatusClasses();
    }

    if (onSitePlanClick !== null) {
      const { svgElement: hitSvg, hitRect } = buildHitSvg(sitePlan);
      hitSvgElement = hitSvg;
      bindHitSvgListeners(hitRect);
      panes.overlayMouseTarget.appendChild(hitSvgElement);
    }

    isAdded = true;
    readyCallback?.();
    readyCallback = null;
  };

  overlayView.draw = function () {
    if (!mainSvgElement) {
      return;
    }

    const projection = overlayView.getProjection();
    if (!projection) {
      return;
    }

    const {
      topRightLatitude, topRightLongitude,
      bottomLeftLatitude, bottomLeftLongitude,
      rotation,
    } = sitePlan;

    const southWest = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(bottomLeftLatitude, bottomLeftLongitude)
    );
    const northEast = projection.fromLatLngToDivPixel(
      new google.maps.LatLng(topRightLatitude, topRightLongitude)
    );
    if (!southWest || !northEast) {
      return;
    }

    const pixelRect = computePixelBounds(southWest, northEast, rotation);
    if (!hasPixelRectChanged(lastPixelRect, pixelRect)) {
      return;
    }

    lastPixelRect = pixelRect;
    const { left, top, width, height, transform } = pixelRect;
    applyPositionStyle(mainSvgElement, left, top, width, height, transform);
    if (hitSvgElement) {
      applyPositionStyle(hitSvgElement, left, top, width, height, transform);
    }
  };

  overlayView.onRemove = function () {
    if (hitRectElement) {
      if (boundHandleMouseDown) {
        hitRectElement.removeEventListener('mousedown', boundHandleMouseDown);
      }
      if (boundHandleClick) {
        hitRectElement.removeEventListener('click', boundHandleClick);
      }
      if (boundHandleMouseEnterContainer) {
        hitRectElement.removeEventListener('mouseenter', boundHandleMouseEnterContainer);
      }
      if (boundHandleMouseLeaveContainer) {
        hitRectElement.removeEventListener('mouseleave', boundHandleMouseLeaveContainer);
      }
    }

    if (hitSvgElement?.parentNode) {
      hitSvgElement.parentNode.removeChild(hitSvgElement);
    }
    if (containerElement?.parentNode) {
      containerElement.parentNode.removeChild(containerElement);
    }

    // Remove the community wrapper only when this was its last child.
    if (communityWrapperElement && communityWrapperElement.childElementCount === 0) {
      communityWrapperElement.parentNode?.removeChild(communityWrapperElement);
    }

    // Mark every entry as detached before nulling layerEntries so that any
    // in-flight loadingPromise .then() callbacks see mainSvgElement===null and
    // exit early, rather than injecting content into a detached group element.
    // (entry references inside promise closures outlive the Map itself.)
    layerEntries?.forEach((entry) => {
      entry.contentLoaded  = true;  // prevents any retry after detach
      entry.loadingPromise = null;
    });

    mainSvgElement        = null;
    containerElement      = null;
    communityWrapperElement = null;
    hitSvgElement         = null;
    hitRectElement        = null;
    layerEntries          = null;
    lastPixelRect         = null;
    pointerDownPos        = null;
    boundHandleMouseDown           = null;
    boundHandleMouseEnterContainer = null;
    boundHandleMouseLeaveContainer = null;
    boundHandleClick               = null;
    isAdded       = false;
    readyCallback = null;
  };

  return {
    overlayView,

    /**
     * Registers a one-time callback that fires once onAdd has completed and
     * layerEntries is fully populated. If onAdd has already fired, the callback
     * is invoked synchronously. Only one callback is supported per overlay
     * instance; a second call before onAdd fires replaces the first.
     */
    onReady(callBack) {
      if (isAdded) {
        callBack();
      } else {
        readyCallback = callBack;
      }
    },

    setVisibleLayerTypes(layerTypes) {
      const layerTypeSet = new Set(layerTypes);
      layerEntries.forEach((entry, layerType) => {
        const shouldShow = layerTypeSet.has(layerType);
        // Track visibility in entry.visible (explicit state) rather than reading
        // back entry.groupElement.style.display (live DOM query coupling control
        // flow to layout state).
        if (shouldShow && !entry.visible) {
          entry.visible = true;
          entry.groupElement.style.display = '';
          if (!entry.contentLoaded) {
            loadAndInjectLayerContent(layerType);
          }
        } else if (!shouldShow && entry.visible) {
          entry.visible = false;
          entry.groupElement.style.display = 'none';
        }
      });
    },

    syncData(newSitePlan, newInteractiveData) {
      const positionChanged =
        newSitePlan.topRightLatitude    !== sitePlan.topRightLatitude    ||
        newSitePlan.topRightLongitude   !== sitePlan.topRightLongitude   ||
        newSitePlan.bottomLeftLatitude  !== sitePlan.bottomLeftLatitude  ||
        newSitePlan.bottomLeftLongitude !== sitePlan.bottomLeftLongitude ||
        newSitePlan.rotation            !== sitePlan.rotation;

      sitePlan = newSitePlan;

      if (newInteractiveData !== interactiveData && lotRenderer) {
        interactiveData = newInteractiveData;
        const lotData  = lotRenderer.buildLotData(interactiveData?.sitePlanLots);
        lotStatusIndex = lotData.lotStatusIndex;
        applyLotStatusClasses();
      }

      if (positionChanged) {
        lastPixelRect = null;
        overlayView.draw();
      }
    },
  };
}

// ─── Facade extension ──────────────────────────────────────────────────────────

/**
 * Extends the map facade with SVG layer overlay support.
 *
 * Exposes:
 *   createSvgLayerOverlay(sitePlan, interactiveData, handlers)
 *     → { attach, detach, syncData(sitePlan, interactiveData),
 *          setVisibleLayerTypes(types) }
 *
 * handlers:
 *   onSitePlanClick({ sitePlanId }) — fired on click (drag-suppressed)
 *   classNames                      — CSS class name map
 *   lotRenderer                     — lot domain callbacks
 */
export default function svgOverlays(facade, adapterState) {
  function createSvgLayerOverlay(sitePlan, interactiveData, handlers) {
    const google      = adapterState.google;
    const mapInstance = adapterState.map;

    if (!google || !mapInstance) {
      return null;
    }

    const { overlayView, ...publicApi } = buildSvgLayerOverlay(
      sitePlan, interactiveData, handlers, google
    );

    return {
      attach() {
        overlayView.setMap(mapInstance);
      },
      detach() {
        overlayView.setMap(null);
      },
      ...publicApi,
    };
  }

  return {
    ...facade,
    createSvgLayerOverlay,
  };
}
